import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowUp, GitBranch, Hammer, Paperclip, Wand2 } from "lucide-react";
import { getStoredMode, setStoredMode, PromptInputMode } from "@/lib/promptBuilderUtils";
import AttachmentChips from "@/components/prompt-builder/AttachmentChips";
import cosmoCommandLogo from "@/assets/scene-5.gif";
import type { AttachmentState } from "@/hooks/useBuilderAttachments";

interface PromptInputProps {
  onAnalyze: (prompt: string) => void;
  onBuildPrompt: (initialGoal: string) => void;
  onBuildWorkflow?: (initialGoal: string) => void;
  isLoading: boolean;
  attachments?: AttachmentState[];
  onFileSelect?: (files: FileList) => void;
  onRemoveAttachment?: (id: string) => void;
  acceptString?: string;
  isUploading?: boolean;
  showAttachments?: boolean;
  greetingName?: string;
  mode?: PromptInputMode;
  onModeChange?: (mode: PromptInputMode) => void;
}

const PromptInput = ({
  onAnalyze,
  onBuildPrompt,
  onBuildWorkflow,
  isLoading,
  attachments = [],
  onFileSelect,
  onRemoveAttachment,
  acceptString,
  isUploading = false,
  showAttachments = false,
  greetingName,
  mode: controlledMode,
  onModeChange,
}: PromptInputProps) => {
  const [prompt, setPrompt] = useState("");
  const [localMode, setLocalMode] = useState<PromptInputMode>('build');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load stored mode on mount
  useEffect(() => {
    if (!controlledMode) {
      setLocalMode(getStoredMode());
    }
  }, [controlledMode]);

  const mode = controlledMode || localMode;

  const handleModeChange = (newMode: PromptInputMode) => {
    if (controlledMode) {
      onModeChange?.(newMode);
    } else {
      setLocalMode(newMode);
      setStoredMode(newMode);
    }
  };

  const handleSubmit = () => {
    if (!prompt.trim()) return;

    if (mode === 'build') {
      onBuildPrompt(prompt);
    } else if (mode === 'improve') {
      onAnalyze(prompt);
    } else {
      onBuildWorkflow?.(prompt);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && onFileSelect) {
      onFileSelect(e.target.files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isBuildMode = mode === 'build';
  const isImproveMode = mode === 'improve';
  const isWorkflowMode = mode === 'workflow';
  const canAttach = isBuildMode && showAttachments && onFileSelect;

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center gap-3 text-left">
        <img
          src={cosmoCommandLogo}
          alt=""
          className="h-[66px] w-[66px] shrink-0 rounded-full object-contain"
          aria-hidden="true"
        />
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Good {(() => { const h = new Date().getHours(); return h < 12 ? "morning" : h < 18 ? "afternoon" : "evening"; })()}
            {greetingName ? `, ${greetingName}` : ""}.
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Start a prompt, sharpen one you already have, or automate a recurring task.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-3 shadow-[0_24px_80px_-48px_hsl(var(--primary))] md:p-4">
        <div className="mb-3 inline-flex rounded-md border border-border bg-muted/30 p-1">
          <Button
            type="button"
            variant={isBuildMode ? "default" : "ghost"}
            size="sm"
            onClick={() => handleModeChange('build')}
            className="h-8 gap-1.5 rounded"
          >
            <Hammer className="h-4 w-4" />
            Build
          </Button>
          <Button
            type="button"
            variant={isImproveMode ? "default" : "ghost"}
            size="sm"
            onClick={() => handleModeChange('improve')}
            className="h-8 gap-1.5 rounded"
          >
            <Wand2 className="h-4 w-4" />
            Improve
          </Button>
          <Button
            type="button"
            variant={isWorkflowMode ? "default" : "ghost"}
            size="sm"
            onClick={() => handleModeChange('workflow')}
            className="h-8 gap-1.5 rounded"
          >
            <GitBranch className="h-4 w-4" />
            Workflow
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide ${isWorkflowMode ? "bg-primary-foreground/20 text-primary-foreground" : "bg-primary/15 text-primary"}`}>Beta</span>
          </Button>
        </div>

        <textarea
          id="prompt-command-composer"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder={
            isBuildMode
              ? "What do you want to build? Example: Turn our Q2 notes into a crisp executive update..."
              : isImproveMode
                ? "Paste a prompt or chat draft you want Cosmo to improve..."
                : "What repeatable process do you want to map? Example: Turn customer calls into CRM updates..."
          }
          className="min-h-28 w-full resize-none rounded-md border border-border/70 bg-background px-4 py-3 text-base leading-relaxed text-foreground placeholder:text-muted-foreground/55 transition-colors focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
        />

        {canAttach && attachments.length > 0 && onRemoveAttachment && (
          <div className="mt-2">
            <AttachmentChips attachments={attachments} onRemove={onRemoveAttachment} />
          </div>
        )}

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {canAttach && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 transition-colors hover:text-foreground disabled:opacity-50"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach context
              </button>
            )}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!prompt.trim() || isLoading || isUploading}
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full"
            aria-label={isBuildMode ? "Build prompt" : isImproveMode ? "Improve prompt" : "Build workflow"}
          >
            {isLoading ? (
              <div className="h-4 w-4 rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground animate-spin" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
          </Button>
        </div>

        {canAttach && (
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptString}
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
        )}
      </div>
    </div>
  );
};

export default PromptInput;
